import { getLogger } from '@utils/general';
import { client } from '@utils/graphql/setup';
import * as is from 'is';
import { createAcceptedProperties, dataRegex } from '../helpers';
import streamsData from '../mocks/streams.json';
import streamsIdData from '../mocks/streamsId.json';
import { AppliancesQuery } from './appliances';
import {
  CriteriaCreator,
  MutationCreator,
  QueryCreator,
  SingleRecordMutationCreator,
} from './base';
import { SourcesQuery } from './sources';
import { ZonesQuery } from './zones';

const logger = getLogger('src.utils.graphQL.creators.streams');

const acceptedProperties = createAcceptedProperties(
  'id',
  'name',
  'description',
  'pullOnRequest',
  'pull_on_request',
  'status',
  'online',
  'use_interleaved_rtsp_transport',
  'lowLatencyPullOnRequest',
  'zoneName',
  'jurisdiction',
  'hlsAddress',
  'hlsSecureAddress',
  'hls_fmp4_address',
  'hls_fmp4_secure_address',
  'hlsPrerollAddress',
  'hlsSecurePrerollAddress',
  'clspsAddress',
  'rtmpAddress',
  'rtmpSecureAddress',
  'rtsp_address',
  'rtspSecureAddress',
  'rtspLowLatencyAddress',
  'distanceFromOrigin',
  'connectionNumber',
  'snapshot_interval',
  'zone.name',
  'appliance.name',
  'appliance.public_address',
  'appliance.management_address',
  'source.device.name',
  'source.device.id',
  'source.appliance.name',
  'source.appliance.id'
);

const acceptedCriteria = {
  id: { type: 'string' },
  name: { type: 'string' },
  description: { type: 'string', triplequoted: true },
  status: { type: 'enum' },
  online: { type: 'boolean' },
  useInterleavedRTSPTransport: { type: 'boolean' },
  pullOnRequest: { type: 'boolean' },
  lowLatencyPullOnRequest: { type: 'boolean' },
  snapshot_interval: { type: 'number' },
  snapshotInterval: { type: 'number' },
  sourceId: { type: 'string' },
  applianceId: { type: 'string' },
  zoneId: { type: 'string' },
  zoneName: { type: 'string' },
  zone_name: { type: 'string' },
  sourceName: { type: 'string' },
  uri: { type: 'string' },
  mcastAddress: { type: 'string' },
  mcastPort: { type: 'number' },
  applianceName: { type: 'string' },
  jurisdiction: { type: 'string' },
  or: { type: 'or' },
  and: { type: 'and' },
  not: { type: 'not' },
};

class StreamsCriteria extends CriteriaCreator {
  constructor(criteria = {}) {
    super(criteria);
  }
  get acceptedCriteria() {
    return acceptedCriteria;
  }
}

export class StreamsQuery extends QueryCreator {
  #applianceQuery = new AppliancesQuery();
  #sourceQuery = null;
  #zoneQuery = new ZonesQuery();
  //#deviceQuery = new DevicesQuery();
  #zonePriority = null;

  constructor(queryType = 'streams', id = null, options = {}) {
    super(queryType, StreamsCriteria);

    // Set the ID if provided
    if (id) {
      this.setId(id);
    }

    // Set pagination if provided
    if (options?.pagination) {
      const {
        count_per_page, current_page, start_on_page,
      } = options.pagination;
      if (count_per_page) this.addPagination('countPerPage', count_per_page);
      if (start_on_page) this.addPagination('startOnPage', start_on_page);
      if (current_page) this.addPagination('startOnPage', current_page); // alias for current_page
    }
    // Always add pagination return fields
    this.addPaginationReturn('total_pages', 'total_records', 'count_per_page', 'current_page');

    // Set sort if provided
    if (options?.sort) {
      this.setSort(options.sort.field, options.sort.direction);
    }

    // Set zone_priority if provided
    if (options?.zone_priority) {
      this.setZonePriority(options.zone_priority);
    }

    // Default behavior: Instantiate sourceQuery unless explicitly told not to
    if (options.includeSourceQuery === false) {
      this.#sourceQuery = null; // Disable sourceQuery if specified
    } else {
      this.#sourceQuery = new SourcesQuery(); // Default: Instantiate sourceQuery
    }
  }

  // Optionally expose the sourceQuery as a getter if you need to access it
  get sourceQuery() {
    return this.#sourceQuery;
  }

  /**
   * Sets the zone name to prioritize in the query.
   * When set, adds zone_priority: "<name>" to the query arguments and
   * sorts by online status so prioritized-zone streams appear first.
   *
   * @param {string} zoneName The zone name to prioritize.
   * @chainable
   */
  setZonePriority(zoneName) {
    if (is.string(zoneName) && !is.empty(zoneName)) {
      this.#zonePriority = zoneName;
    }
    return this;
  }

  extraArgs() {
    if (this.#zonePriority) {
      return [`zone_priority: "${this.#zonePriority}"`];
    }
    return [];
  }

  get acceptedReturnProperties() {
    return acceptedProperties;
  }

  addReturnProperties(...properties) {
    for (const property of properties) {
      if (property.startsWith('zone')) {
        const [, ...zoneProperty] = property.split('.');
        this.#zoneQuery.addReturnProperties(zoneProperty.join('.'));
        continue;
      }
      if (property.startsWith('source')) {
        const [, ...sourceProperty] = property.split('.');
        this.#sourceQuery.addReturnProperties(sourceProperty.join('.'));
        continue;
      }
      if (property.startsWith('appliance')) {
        const [, ...applianceProperty] = property.split('.');
        this.#applianceQuery.addReturnProperties(applianceProperty.join('.'));
        continue;
      }
      super.addReturnProperties(property);
    }
    return this;
  }
  returnSyntax() {
    const syntax = super.returnSyntax();
    if (syntax === '') {
      return syntax;
    }

    const [, data] = syntax.match(dataRegex);
    const zoneSyntax = this.#zoneQuery.returnSyntax();
    const applianceSyntax = this.#applianceQuery.returnSyntax();

    // Handle the case when sourceQuery is disabled
    let sourceSyntax = '';
    if (this.#sourceQuery) {
      sourceSyntax = this.#sourceQuery.returnSyntax();
    }

    const dataSyntax = [data.trim()];

    if (!is.empty(zoneSyntax)) {
      dataSyntax.push(
        zoneSyntax.replace(/^data/, 'zone').replace(/\n$/, '').trim()
      );
    }

    // Only add sourceSyntax if sourceQuery is enabled
    if (!is.empty(sourceSyntax)) {
      dataSyntax.push(
        sourceSyntax.replace(/^data/, 'source').replace(/\n$/, '').trim()
      );
    }

    if (!is.empty(applianceSyntax)) {
      dataSyntax.push(
        applianceSyntax.replace(/^data/, 'appliance').replace(/\n$/, '').trim()
      );
    }

    return `data {\n${dataSyntax.join('\n')}\n}`;
  }
}

const getStreamFieldValue = (stream, key) => {
  switch (key) {
    case 'zone_name':
      return stream.zone?.name ?? '';
    case 'appliance_name':
      return stream.appliance?.name ?? '';
    case 'source_name':
      return stream.source?.name ?? '';
    case 'zone_id':
      return stream.zone?.id ?? '';
    case 'appliance_id':
      return stream.appliance?.id ?? '';
    case 'source_id':
      return stream.source?.id ?? '';
    case 'uri':
      return stream.source?.uri ?? '';
    case 'mcast_address':
      return stream.source?.sdp_input ?? '';
    case 'mcast_port':
      return stream.source?.port ?? '';
    default:
      return stream[key];
  }
};

const parseRegex = (value) => {
  if (typeof value !== 'string' || !value.startsWith('regex:')) return null;
  const raw = value.slice('regex:'.length);
  const caseInsensitive = raw.startsWith('(?i)');
  const pattern = caseInsensitive ? raw.slice(4) : raw;
  try {
    return new RegExp(pattern, caseInsensitive ? 'i' : undefined);
  } catch {
    return null;
  }
};

const compareValue = (stream, key, expected) => {
  const actual = getStreamFieldValue(stream, key);
  if (Array.isArray(expected)) {
    return expected.some((item) => compareValue(stream, key, item));
  }
  if (typeof expected === 'string' && expected.startsWith('regex:')) {
    const regex = parseRegex(expected);
    return regex ? regex.test(String(actual ?? '')) : false;
  }
  return actual === expected;
};

const matchesCriteria = (stream, criteria) => {
  if (!criteria || typeof criteria !== 'object') return false;
  if ('or' in criteria && Array.isArray(criteria.or)) {
    return criteria.or.some((item) => matchesCriteria(stream, item));
  }
  if ('and' in criteria && Array.isArray(criteria.and)) {
    return criteria.and.every((item) => matchesCriteria(stream, item));
  }
  if ('not' in criteria) {
    return !matchesCriteria(stream, criteria.not);
  }
  return Object.entries(criteria).every(([key, value]) => compareValue(stream, key, value));
};

export function getStreams(id, criteria = {}, options = {}) {
  let query;
  if (id) {
    query = new StreamsQuery('stream', id, options);
    query.setId(id);
  } else {
    query = new StreamsQuery('streams', { includeSourceQuery: true }, options);

    if (!is.empty(criteria)) {
      for (const [key, value] of Object.entries(criteria)) {
        query.addCriteria(key, value);
      }
    }
  }

  return {
    query,
    execute() {
      return /^dev(?:elop(?:ment)?)?$/i.test(import.meta.env.VITE_ENV)
        ? Promise.resolve().then(() => {
          if (id) {
            return { data: streamsIdData.find((stream) => stream.id === id) };
          }
          if (!criteria || Object.keys(criteria).length === 0) {
            return { data: streamsData };
          }

          const filtered = streamsData.filter((stream) => matchesCriteria(stream, criteria));
          return { data: filtered };
        })
        : client.query(query.name, query.toString());
    },
  };
}

export class SingleStreamMutation extends SingleRecordMutationCreator {
  constructor(queryType = 'create_stream', criteria = {}) {
    super(queryType, StreamsCriteria);
    this.setCriteria(criteria);
  }
  get acceptedReturnProperties() {
    return acceptedProperties;
  }
}

export class SaveStreamMutation extends MutationCreator {
  constructor(queryType = 'create_stream', criteria = {}) {
    super(queryType, StreamsCriteria);
    this.setCriteria(criteria);
  }
  get acceptedReturnProperties() {
    return acceptedProperties;
  }
}

export function saveStream(mutationType, input) {
  let mutation;
  mutation = new SaveStreamMutation(mutationType, input);

  return {
    mutation,
    execute() {
      return client.mutate(mutation.name, mutation.toString());
    },
  };
}

export function updateStream(mutationType, id, input = {}, bulkUpdate = false, streamInfo = null) {
  let mutation;
  mutation = new SingleStreamMutation(mutationType, input);
  mutation.setId(id);

  return {
    mutation,
    execute() {
      return client.mutate(mutation.name, mutation.toString(), bulkUpdate, streamInfo);
    },
  };
}

export const deleteStream = async (stream) => {
  const { execute } = updateStream('remove_stream', stream.id);

  try {
    return await execute();
  }
  catch (error) {
    logger.error(error, `Failed to delete stream ${stream.id}.`);
    throw new Error(error);
  }
};